/*
 * Penguin - Complete your comfort set (v2)
 * For Dawn-based themes (Dawn 10+). No dependencies.
 *
 * Analytics hook:  document.addEventListener('penguin:fbt:added', (e) => e.detail.items)
 */
(() => {
  if (customElements.get('penguin-fbt')) return;

  /* ---------- Helpers ---------- */

  // [decimals, thousands separator, decimal separator]
  const MONEY_STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };

  function formatMoney(cents, format) {
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    const style = MONEY_STYLES[match ? match[1] : 'amount'] || MONEY_STYLES.amount;
    const [decimals, thousands, decimal] = style;
    const [whole, fraction] = (Number(cents || 0) / 100).toFixed(decimals).split('.');
    const value = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    return match ? format.replace(match[0], value) : value;
  }

  const fill = (text, values) =>
    String(text).replace(/\{(\w+)\}/g, (_, key) => (key in values ? values[key] : ''));

  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  // Dawn's pub/sub (constants.js + pubsub.js). Optional: the widget works without it.
  const hasPubSub = () =>
    typeof subscribe === 'function' && typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object';

  const reduceMotion = () =>
    !window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const easeOut = (p) => 1 - Math.pow(1 - p, 3);
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* ---------- Component ---------- */

  class PenguinFbt extends HTMLElement {
    connectedCallback() {
      if (this.started || this.closest('quick-add-modal, .quick-add-modal')) return;
      const configNode = this.querySelector('[data-pfbt-config]');
      if (!configNode) return;
      this.started = true;

      this.cfg = JSON.parse(configNode.textContent);
      this.t = this.cfg.i18n;
      this.variantCache = new WeakMap();
      this.cartTotal = null;
      this.cartVariants = null;
      this.selectedTotal = 0;
      this.shownTotal = null;
      this.busy = false;

      this.list = this.querySelector('[data-pfbt-list]');
      this.button = this.querySelector('[data-pfbt-add]');
      this.buttonLabel = this.querySelector('[data-pfbt-label]');
      this.message = this.querySelector('[data-pfbt-msg]');
      this.saveEl = this.querySelector('[data-pfbt-save]');
      this.ship = this.querySelector('[data-pfbt-ship]');

      // Text nodes updated in place: smooth count-up, and no churn for other scripts watching the DOM.
      this.totalText = this.querySelector('[data-pfbt-total]').appendChild(document.createTextNode(''));
      this.saveText = this.saveEl.appendChild(document.createTextNode(''));
      if (this.ship) this.shipText = this.ship.querySelector('[data-pfbt-ship-text]').appendChild(document.createTextNode(''));

      this.addEventListener('change', (event) => this.onChange(event));
      this.button.addEventListener('click', () => this.addToCart());
      this.listenToMainProduct();
      this.listenToCart();

      if (this.cfg.needsFetch) this.loadRecommendations();
      else this.start([]);
    }

    get rows() {
      return Array.from(this.list.querySelectorAll('[data-pfbt-item]'));
    }

    get mainRow() {
      return this.list.querySelector('[data-pfbt-item][data-main]');
    }

    checkbox(row) {
      return row.querySelector('.pfbt__check');
    }

    variants(row) {
      if (!this.variantCache.has(row)) {
        const data = row.querySelector('[data-pfbt-variants]');
        this.variantCache.set(row, data ? JSON.parse(data.textContent) : {});
      }
      return this.variantCache.get(row);
    }

    variant(row) {
      return this.variants(row)[row.dataset.variantId];
    }

    money(cents) {
      return formatMoney(cents, this.cfg.moneyFormat);
    }

    /* ---------- Loading picks ---------- */

    async loadRecommendations() {
      const plan = { curated: ['complementary'], automatic: ['related'] };
      const intents = plan[this.cfg.source] || ['complementary', 'related'];
      let added = [];

      try {
        for (const intent of intents) {
          const rows = await this.fetchRows(intent);
          if (rows.length) {
            const anchor = this.list.querySelector('[data-pfbt-skeleton]');
            rows.forEach((row) => this.list.insertBefore(row, anchor));
            added = rows;
            break;
          }
        }
      } catch (error) {
        console.warn('[penguin-fbt] Could not load recommendations', error);
      }

      this.list.querySelectorAll('[data-pfbt-skeleton]').forEach((node) => node.remove());

      // Nothing to pair with: hide the box rather than show a lonely product.
      if (this.rows.length < 2) {
        this.hidden = true;
        return;
      }
      this.start(added);
    }

    async fetchRows(intent) {
      const params = new URLSearchParams({
        section_id: this.cfg.sectionId,
        product_id: this.cfg.productId,
        limit: Math.min(this.cfg.maxItems + 3, 10), // room for sold-out items the section skips
        intent,
      });
      const response = await fetch(`${this.cfg.recsUrl}?${params}`);
      if (!response.ok) return [];

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const mainId = String(this.cfg.productId);
      return Array.from(doc.querySelectorAll('[data-pfbt-source] [data-pfbt-item]'))
        .filter((row) => row.dataset.productId !== mainId)
        .slice(0, this.cfg.maxItems)
        .map((row) => document.importNode(row, true));
    }

    start(newRows) {
      this.rows.forEach((row) => this.paintRow(row));
      this.update();
      this.classList.add('is-ready');
      this.setupEntrance(newRows);
      this.refreshCart();
    }

    /* ---------- Entrance: one orchestrated moment ---------- */

    setupEntrance(newRows) {
      if (reduceMotion() || typeof IntersectionObserver !== 'function') return;
      const rect = this.getBoundingClientRect();
      const onScreen = rect.top < window.innerHeight && rect.bottom > 0;

      if (onScreen) {
        // Already visible: only the cards that just loaded animate in.
        if (newRows.length) this.playEntrance(newRows, this.rows.length - newRows.length);
        return;
      }

      this.classList.add('is-waiting');
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.playEntrance(this.rows, 0);
        },
        { threshold: 0.2 }
      );
      observer.observe(this);
    }

    playEntrance(rows, offset) {
      rows.forEach((row, index) => {
        const order = index + offset;
        row.style.setProperty('--i', order);
        row.classList.add('is-entering');
        setTimeout(() => row.classList.remove('is-entering'), 900 + order * 90);
      });
      this.classList.remove('is-waiting');
    }

    /* ---------- Cards ---------- */

    onChange(event) {
      const row = event.target.closest('[data-pfbt-item]');
      if (!row || !this.contains(row)) return;

      if (event.target.matches('[data-pfbt-select]')) {
        row.dataset.variantId = event.target.value;
        this.paintRow(row);
      } else if (event.target.matches('.pfbt__check')) {
        row.dataset.touched = '1'; // the customer decided; stop auto-ticking this card
        delete row.dataset.autoOff;
      }
      row.classList.toggle('is-off', !this.checkbox(row).checked);
      this.update();
    }

    paintRow(row) {
      const variant = this.variant(row);
      if (!variant) return;

      const checkbox = this.checkbox(row);
      const wasSoldOut = row.classList.contains('is-sold-out');
      if (!variant.available) checkbox.checked = false;
      else if (wasSoldOut) checkbox.checked = true;
      checkbox.disabled = !variant.available;
      row.classList.toggle('is-sold-out', !variant.available);
      row.classList.toggle('is-off', !checkbox.checked);

      row.querySelector('[data-pfbt-price]').replaceChildren(...this.priceNodes(variant));

      const image = row.querySelector('.pfbt__img');
      if (image && variant.image && image.getAttribute('src') !== variant.image) image.src = variant.image;

      const title = row.querySelector('[data-pfbt-variant-title]');
      if (title) title.textContent = variant.title;
      const chip = row.querySelector('[data-pfbt-chip]');
      if (chip) chip.textContent = variant.title;

      if (row === this.mainRow) this.markMainInCart();
    }

    priceNodes(variant) {
      if (!variant.available) return [make('span', 'pfbt__soldout', this.t.soldOut)];

      const onSale = variant.compare > variant.price;
      const now = make('span', 'pfbt__now');
      if (onSale) now.append(make('span', 'pfbt-vh', `${this.t.salePrice} `));
      now.append(this.money(variant.price));
      if (!onSale) return [now];

      const was = make('s', 'pfbt__was');
      was.append(make('span', 'pfbt-vh', `${this.t.regularPrice} `), this.money(variant.compare));
      return [now, was];
    }

    // If the product the customer is viewing is already in the cart, untick it
    // so the set doesn't add a second one by accident. The customer can re-tick it.
    markMainInCart() {
      const row = this.mainRow;
      if (!row) return;
      const inCart = !!this.cartVariants && this.cartVariants.has(String(row.dataset.variantId));
      row.classList.toggle('is-in-cart', inCart);

      const tag = row.querySelector('[data-pfbt-tag]');
      if (tag) tag.textContent = inCart ? this.t.inCart : this.t.yourPick;

      const box = this.checkbox(row);
      if (row.dataset.touched || box.disabled) return;
      if (inCart && box.checked) {
        box.checked = false;
        row.dataset.autoOff = '1';
      } else if (!inCart && row.dataset.autoOff) {
        box.checked = true;
        delete row.dataset.autoOff;
      }
      row.classList.toggle('is-off', !box.checked);
    }

    /* ---------- Totals, savings, shipping, button ---------- */

    selectedRows() {
      return this.rows.filter((row) => this.checkbox(row).checked);
    }

    update() {
      const picked = this.selectedRows();
      let total = 0;
      let regular = 0;
      picked.forEach((row) => {
        const variant = this.variant(row);
        if (!variant) return;
        total += variant.price;
        regular += Math.max(variant.compare, variant.price);
      });
      this.selectedTotal = total;
      this.animateTotal(total);

      const was = this.querySelector('[data-pfbt-total-was]');
      was.hidden = regular <= total;
      was.querySelector('[data-pfbt-value]').textContent = this.money(regular);

      const saving = regular - total;
      this.saveEl.hidden = saving <= 0;
      const saveLabel = fill(this.t.save, { amount: this.money(saving) });
      if (this.saveText.nodeValue !== saveLabel) {
        this.saveText.nodeValue = saveLabel;
        this.bump(this.saveEl);
      }

      const count = picked.length;
      const all = count === this.rows.length;
      let label = this.t.addNone;
      if (count === 1) label = this.t.addOne;
      else if (count === 2) label = this.t.addBoth;
      else if (count > 2 && all) label = fill(this.t.addAll, { count });
      else if (count > 2) label = fill(this.t.addSome, { count });

      this.idleLabel = label;
      if (!this.busy && !this.button.classList.contains('is-added')) {
        this.buttonLabel.textContent = label;
        this.button.disabled = count === 0;
      }
      this.updateShipping();
    }

    animateTotal(to) {
      const from = this.shownTotal;
      this.shownTotal = to;
      if (this.totalFrame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.totalFrame);
      if (from == null || from === to || reduceMotion() || typeof requestAnimationFrame !== 'function') {
        this.totalText.nodeValue = this.money(to);
        return;
      }
      const startTime = performance.now();
      const duration = 420;
      const step = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        this.totalText.nodeValue = this.money(Math.round(from + (to - from) * easeOut(progress)));
        if (progress < 1) this.totalFrame = requestAnimationFrame(step);
      };
      this.totalFrame = requestAnimationFrame(step);
    }

    updateShipping() {
      if (!this.ship) return;
      const threshold = Number(this.cfg.freeShipping) || 0;
      if (!(threshold > 0) || this.cartTotal === null) return;

      const combined = this.cartTotal + this.selectedTotal;
      let text;
      let free = true;
      if (this.cartTotal >= threshold) text = this.t.shipFree;
      else if (combined >= threshold) text = this.t.shipUnlock;
      else {
        free = false;
        text = fill(this.t.shipMore, { amount: this.money(threshold - combined) });
      }

      const wasFree = this.ship.classList.contains('is-free');
      const wasLoading = this.ship.classList.contains('is-loading');
      this.ship.classList.remove('is-loading');
      this.ship.classList.toggle('is-free', free);
      if (free && !wasFree && !wasLoading) {
        this.ship.classList.remove('is-celebrate');
        void this.ship.offsetWidth;
        this.ship.classList.add('is-celebrate');
      }
      this.shipText.nodeValue = text;
      this.ship.querySelector('[data-pfbt-bar]').style.width = `${Math.min(100, (combined / threshold) * 100)}%`;
    }

    bump(element) {
      if (reduceMotion()) return;
      element.classList.remove('is-bump');
      void element.offsetWidth;
      element.classList.add('is-bump');
    }

    async refreshCart() {
      try {
        const response = await fetch(`${this.cfg.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.cartTotal = Number(cart.total_price) || 0;
        this.cartVariants = new Set((cart.items || []).map((item) => String(item.variant_id)));
      } catch (error) {
        this.cartTotal = null;
        this.cartVariants = null;
      }
      this.markMainInCart();
      this.update();
    }

    /* ---------- Keep "Your pick" in sync with the main variant picker ---------- */

    listenToMainProduct() {
      const apply = (id) => {
        const row = this.mainRow;
        if (!row || id == null) return;
        const key = String(id);
        if (row.dataset.variantId === key || !this.variants(row)[key]) return;
        row.dataset.variantId = key;
        this.paintRow(row);
        this.update();
      };

      // Dawn sets the hidden input[name="id"] and fires "change" on every variant switch.
      document.addEventListener('change', (event) => {
        const field = event.target;
        if (field && field.name === 'id' && !this.contains(field)) apply(field.value);
      });

      // Dawn 15+ also publishes variantChange.
      if (hasPubSub() && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => apply(event?.data?.variant?.id));
      }

      apply(this.liveMainVariantId());
    }

    liveMainVariantId() {
      const row = this.mainRow;
      if (!row) return null;
      const known = this.variants(row);
      const field = Array.from(document.querySelectorAll('form[action*="/cart/add"] [name="id"]')).find(
        (input) => !this.contains(input) && known[input.value]
      );
      return field ? field.value : null;
    }

    listenToCart() {
      // When the main Add to cart is used, refresh so "Your pick" becomes "In your cart".
      if (hasPubSub() && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'penguin-fbt') this.refreshCart();
        });
      }
    }

    /* ---------- Add to cart ---------- */

    async addToCart() {
      if (this.busy) return;

      // Read the main variant at click time so we never add the wrong size or colour.
      const live = this.liveMainVariantId();
      if (live && this.mainRow && this.mainRow.dataset.variantId !== live) {
        this.mainRow.dataset.variantId = live;
        this.paintRow(this.mainRow);
        this.update();
      }

      const picked = this.selectedRows();
      const items = picked.map((row) => ({ id: Number(row.dataset.variantId), quantity: 1 }));
      if (!items.length) return;

      const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections =
        cartUi && typeof cartUi.getSectionsToRender === 'function'
          ? cartUi.getSectionsToRender().map((section) => section.id)
          : [];

      this.setBusy(true);
      this.say('');

      let data;
      try {
        const response = await fetch(`${this.cfg.cartAddUrl}.js`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        data = await response.json();
        if (!response.ok || data.status) {
          this.fail(data.description || data.message);
          return;
        }
      } catch (error) {
        this.fail();
        return;
      }

      this.dispatchEvent(new CustomEvent('penguin:fbt:added', { bubbles: true, detail: { items: data.items || [] } }));
      this.tagCart();

      if (hasPubSub() && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'penguin-fbt', productVariantId: items[0].id, cartData: data });
      }

      this.setBusy(false, this.t.added);
      await this.flyToCart(picked);

      // Open the theme's own cart drawer. If the theme has none, go to the cart page.
      try {
        if (!cartUi || typeof cartUi.renderContents !== 'function' || !data.sections) throw new Error('no cart ui');
        cartUi.classList.remove('is-empty');
        const first = (data.items && data.items[0]) || {};
        cartUi.renderContents({ ...data, id: first.id, key: first.key });
      } catch (error) {
        window.location.href = this.cfg.cartUrl;
        return;
      }

      this.say(this.t.added);
      this.refreshCart();
    }

    // Product photos arc into the header cart icon, then the icon bounces.
    async flyToCart(rows) {
      if (reduceMotion()) return;
      const target = document.querySelector('#cart-icon-bubble');
      if (!target || typeof target.animate !== 'function') return;
      const to = target.getBoundingClientRect();
      if (!to.width || to.bottom < 0 || to.top > window.innerHeight) return;

      const endX = to.left + to.width / 2;
      const endY = to.top + to.height / 2;

      const flights = rows.map((row, index) => {
        const frame = row.querySelector('.pfbt__frame');
        if (!frame) return null;
        const from = frame.getBoundingClientRect();
        if (!from.width) return null;

        const ghost = frame.cloneNode(true);
        ghost.classList.add('pfbt__ghost');
        Object.assign(ghost.style, {
          left: `${from.left}px`,
          top: `${from.top}px`,
          width: `${from.width}px`,
          height: `${from.height}px`,
        });
        document.body.appendChild(ghost);

        const dx = endX - (from.left + from.width / 2);
        const dy = endY - (from.top + from.height / 2);
        const flight = ghost.animate(
          [
            { transform: 'translate(0, 0) scale(1)', opacity: 1 },
            { transform: `translate(${dx * 0.4}px, ${dy * 0.4 - 90}px) scale(0.6)`, opacity: 1, offset: 0.45 },
            { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0.2 },
          ],
          { duration: 700, delay: index * 80, easing: 'cubic-bezier(0.55, 0, 0.3, 1)', fill: 'forwards' }
        );
        return flight.finished.catch(() => {}).then(() => ghost.remove());
      });

      await Promise.all(flights.filter(Boolean));
      target.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }],
        { duration: 420, easing: 'cubic-bezier(0.3, 1.6, 0.5, 1)' }
      );
    }

    // Marks the order so you can count sales from this box
    // (Orders > order > "Additional details").
    tagCart() {
      fetch(`${this.cfg.cartUpdateUrl}.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ attributes: { 'Bought together widget': 'Yes' } }),
      }).catch(() => {});
    }

    fail(message) {
      this.setBusy(false);
      this.say(message || this.t.error, true);
    }

    setBusy(busy, doneLabel) {
      this.busy = busy;
      this.button.classList.toggle('is-busy', busy);
      this.button.classList.toggle('is-added', !!doneLabel);
      this.button.setAttribute('aria-busy', String(busy));
      this.button.disabled = busy || (!doneLabel && this.selectedRows().length === 0);
      this.buttonLabel.textContent = busy ? this.t.adding : doneLabel || this.idleLabel;

      clearTimeout(this.labelTimer);
      if (doneLabel) {
        this.labelTimer = setTimeout(() => {
          this.button.classList.remove('is-added');
          if (this.busy) return;
          this.buttonLabel.textContent = this.idleLabel;
          this.button.disabled = this.selectedRows().length === 0;
        }, 2200);
      }
    }

    say(text, isError = false) {
      this.message.textContent = text;
      this.message.classList.toggle('is-error', isError);
    }
  }

  customElements.define('penguin-fbt', PenguinFbt);
})();